import React, { useRef, useEffect, useState } from 'react';

/**
 * ScrollableTableContainer - A reusable component for large tables with improved scrolling UX
 * 
 * Features:
 * - Fixed header/filters/action buttons area
 * - Independent scrollable table area with both vertical and horizontal scrolling
 * - Synchronized horizontal scrollbar at the top
 * - Sticky table headers during vertical scroll
 * - Works with wide tables and large datasets
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.header - Fixed header content (title, filters, action buttons)
 * @param {React.ReactNode} props.children - Table content to be scrollable
 * @param {string} props.className - Additional CSS classes for the container
 * @param {number} props.maxHeight - Maximum height for the scrollable area (default: 600px)
 * @param {boolean} props.showTopScrollbar - Whether to show synchronized top scrollbar (default: true)
 * @param {boolean} props.stickyHeader - Whether table header should stick during vertical scroll (default: true)
 */
const ScrollableTableContainer = ({
  header,
  children,
  className = '',
  maxHeight = 600,
  showTopScrollbar = true,
  stickyHeader = true,
}) => {
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);

  // Sync scroll between table container and top scrollbar
  const handleTableScroll = (e) => {
    if (topScrollbarRef.current && showTopScrollbar) {
      topScrollbarRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const handleTopScrollbarScroll = (e) => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // Check if horizontal scrollbar is needed & measure exact inner content width
  const checkScrollbarNeeded = () => {
    if (tableContainerRef.current) {
      const scrollEl = tableContainerRef.current;
      const childEl = scrollEl.firstElementChild || scrollEl;
      const actualContentWidth = Math.max(scrollEl.scrollWidth, childEl.scrollWidth, childEl.offsetWidth);
      
      setContentWidth(actualContentWidth);
      setShowScrollbar(actualContentWidth > scrollEl.clientWidth + 1);
    }
  };

  useEffect(() => {
    checkScrollbarNeeded();
    const resizeObserver = new ResizeObserver(() => {
      checkScrollbarNeeded();
    });
    
    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current);
      if (tableContainerRef.current.firstElementChild) {
        resizeObserver.observe(tableContainerRef.current.firstElementChild);
      }
    }

    return () => resizeObserver.disconnect();
  }, [children]);

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Fixed Header Section */}
      {header && (
        <div className="flex-shrink-0 bg-white">
          {header}
        </div>
      )}

      {/* Synchronized Top Scrollbar */}
      {showTopScrollbar && showScrollbar && (
        <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200">
          <div
            ref={topScrollbarRef}
            onScroll={handleTopScrollbarScroll}
            className="overflow-x-auto overflow-y-hidden h-4 cursor-pointer"
            style={{ scrollbarWidth: 'thin' }}
          >
            <div style={{ width: contentWidth > 0 ? `${contentWidth}px` : '100%', height: '1px' }} />
          </div>
        </div>
      )}

      {/* Scrollable Table Area */}
      <div
        ref={tableContainerRef}
        onScroll={handleTableScroll}
        className="scrollable-table-container flex-1 overflow-auto border border-gray-200 rounded-lg"
        style={{ 
          maxHeight: maxHeight,
          overflowX: 'auto',
          overflowY: 'auto'
        }}
      >
        <div className="min-w-full">
          {React.cloneElement(children, {
            className: stickyHeader 
              ? `${children.props.className || ''} sticky-header-enabled`
              : children.props.className
          })}
        </div>
      </div>

      {/* CSS for sticky headers - injected via style tag */}
      <style>{`
        .sticky-header-enabled thead th {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
          background-color: inherit !important;
        }
        
        /* Custom scrollbar styling */
        .scrollable-table-container::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        .scrollable-table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .scrollable-table-container::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        
        .scrollable-table-container::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </div>
  );
};

export default ScrollableTableContainer;
